import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { toast } from 'vue-sonner';

/**
 * Every write the Sales surfaces make against an order action, in one place.
 *
 * Modelled on `useMyOrderActions`'s optimistic-mutation shape: snapshot, patch
 * the cache, roll the snapshot back if the write fails, invalidate on settle.
 *
 * **Query keys.** The listing is rendered by `MuvalTable`, whose key is
 * `[url, filters, tenantId]` — so the *url* is the prefix every Sales-list cache
 * entry shares, regardless of which filters produced it. Patching by prefix is
 * what lets one row change land on whichever filtered page happens to be on
 * screen. The diary key is exported for the diary to build its own query on, so
 * the two surfaces stay invalidated together.
 */
export const SALES_ACTIONS_URL = '/sales/order-actions';

export const SALES_ACTIONS_LIST_KEY = [SALES_ACTIONS_URL];
export const SALES_ACTIONS_DIARY_KEY = ['sales-actions-diary'];

/**
 * The order screen's daily-actions widget. Invalidated but never patched: its
 * rows are a different resource with different fields, so a Sales-shaped patch
 * would write keys it does not use. A refetch is correct and cheap.
 */
export const MY_ORDER_ACTIONS_KEY = ['me/order-actions'];

/**
 * The order screen's Actions tab, keyed by order beneath this prefix. Declared
 * here beside the other order-action keys rather than in `useOrderActions` so the
 * queue and the Sales writes can invalidate it without importing the composable
 * that owns it — the tab is a *reader* of actions these surfaces write, and the
 * dependency only ever points one way.
 */
export const ORDER_ACTIONS_KEY = ['order-actions'];

/**
 * The sales working queue's cache entry. Declared here, beside the other keys a
 * write invalidates, rather than in `useSalesQueue` where it is read — that
 * module already imports this one, so owning the key there and reaching back for
 * it from here would close an import cycle, and `useSalesQueue`'s `DEPENDENT_KEYS`
 * is a module-level const, so the cycle would be a load-order TDZ rather than a
 * lint warning. `useSalesQueue` re-exports it, so its existing importers are
 * unaffected.
 */
export const SALES_QUEUE_ACTIVE_KEY = ['sales-queue-active'];

export type SalesActionStatus = 'TO_DO' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

/**
 * The permitted states, labelled once and in the order they are offered. The
 * status filter, the inline cell and the diary all read this, so none of them
 * can invent a state of their own or spell one differently.
 *
 * `CANCELLED` is the way out of the lifecycle rather than the end of it, and it
 * is offered on every action type — a call or a reminder can be called off just
 * as an inspection can. It sits last for that reason.
 */
export const SALES_ACTION_STATUS_LABELS: Record<SalesActionStatus, string> = {
    TO_DO: 'To do',
    IN_PROGRESS: 'In progress',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
};

/**
 * The roles the assignee filter narrows by, labelled once and in the order they
 * are offered.
 *
 * The tenant's business roles only. The API validates the whole `UserRole`
 * vocabulary, so the platform roles — `SYSTEM`, `SUPER`, `MOVEPRO_ADMIN`,
 * `DEVELOPER` — would be accepted; they are left out because nobody schedules a
 * follow-up for one, and an option that always returns nothing is noise in a
 * filter bar.
 *
 * `FIELD_STAFF` and `CONTRACTOR` are in, and they are the reason the filter is
 * more than a convenience: a site inspection is assigned to the staff member it
 * is booked with, so narrowing to those two is how an operator finds every
 * inspection somebody is attending.
 */
export const SALES_ACTION_ROLE_LABELS: Record<string, string> = {
    OWNER: 'Owner',
    ADMIN: 'Admin',
    MANAGER: 'Manager',
    OPS: 'Operations',
    SALES: 'Sales',
    FINANCE: 'Finance',
    FIELD_STAFF: 'Field staff',
    CONTRACTOR: 'Contractor',
};

/**
 * The state of the action's owning order, as the filter offers it.
 */
export type SalesOrderStateFilter = 'PIPE' | 'BIN' | 'JOB';

/**
 * The owning-order states the filter narrows by, labelled once and in the order
 * they are offered (CU-86d44f765).
 *
 * Three values over six stored states, mirroring the leads listing's *Lead state*
 * filter. **`JOB` is not the `JOB` order state** — it covers all three won forms,
 * confirmed, partially confirmed and closed, because an operator working a sales
 * queue does not distinguish between them. The API validates the same three
 * values and expands `JOB` server-side.
 *
 * `NEW` (a draft order) is deliberately absent: it rarely carries a follow-up, it
 * is returned under the default anyway, and an option that nobody reaches for is
 * noise in a filter bar. The same reasoning keeps the platform roles out of the
 * role map above.
 *
 * Selecting nothing is not "no filter" — it is the default of withholding binned
 * orders' actions. The bar therefore opens empty rather than pre-selecting the
 * two non-binned values, which would read as a filter the operator had applied.
 */
export const SALES_ORDER_STATE_LABELS: Record<SalesOrderStateFilter, string> = {
    PIPE: 'Pipe',
    BIN: 'Bin',
    JOB: 'Job',
};

/**
 * The roles whose Sales page opens on everybody's actions.
 *
 * This decides one thing: the assignee filter the Sales page *starts* with. A
 * role in this list opens on the whole team, which is what the page has always
 * done; every other role opens pre-filtered to their own actions, because that
 * is the queue they came to work. It is the same argument the date window already
 * makes by opening on today rather than on every action ever scheduled.
 *
 * A **UI default, not authorisation.** The filter is an ordinary chip: visible,
 * and removable by anyone who wants the whole team. No request is refused on the
 * strength of this list, and the endpoint still returns every assignee's actions
 * when asked for them.
 *
 * **This is not the `sales-actions-manage` role set, and must not be reconciled
 * with it.** That gate — `AuthServiceProvider::sales-actions-manage` — grants
 * write access to this surface, and it *includes* `SALES`, which is precisely the
 * role this default exists for. Reusing it here would open every sales agent on
 * the whole team again and silently undo this behaviour. The two lists answer
 * different questions and are meant to differ.
 *
 * Stated as the roles that see *everything*, deliberately, rather than as the
 * roles that are narrowed. A role added to the vocabulary later then defaults to
 * seeing only its own actions, which is the conservative direction — an
 * unfamiliar role should not silently inherit the whole tenant's follow-up queue.
 * The positive framing is also what makes `hasAnyRole`'s OWNER/ADMIN short-circuit
 * safe at its default; see the call site in `SalesActionsIndex.vue`.
 *
 * The platform roles are in, unlike in the label map above: `SALES_ACTION_ROLE_LABELS`
 * omits them because nobody is *assigned* an action as a `MOVEPRO_ADMIN`, but one
 * plainly still opens this page — and owning no actions in the tenant is exactly
 * why they must not be narrowed to their own.
 */
export const SALES_ACTION_VIEW_ALL_ROLES = [
    'SUPER',
    'MOVEPRO_ADMIN',
    'SYSTEM',
    'DEVELOPER',
    'OWNER',
    'ADMIN',
    'MANAGER',
    'OPS',
];

export interface SalesActionOrder {
    id: number;
    ref: string | null;
    status: string | null;
    /**
     * The order's state, distinct from its status. Carried so a binned order's
     * action — withheld by default, and returned only when `Bin` is selected — is
     * markable on screen rather than reading as ordinary work.
     */
    state: string | null;
    customer_name: string | null;
    /**
     * The customer's contact details, so a surface rendering this row can dial or
     * email without first opening the order. Both phone forms are on the wire:
     * `customer_phone` is the E164 value, `customer_phone_raw` is what
     * `OrderCustomerCard.vue` builds its `tel:` href from — and the queue footer
     * and the order screen must dial identically.
     */
    customer_phone: string | null;
    customer_phone_raw: string | null;
    customer_email: string | null;
    pickup_locality: string | null;
    delivery_locality: string | null;
    move_status: string | null;
    move_type: string | null;
    /** One entry per quoted service, drawn as the icon cluster the listings share. */
    services?: Array<Record<string, unknown>>;
}

/** One row of the Sales listing, as `SalesActionResource` serialises it. */
export interface SalesAction {
    id: number;
    action_type: string;
    schedule_at: string | null;
    all_day: boolean;
    status: SalesActionStatus;
    completed: boolean;
    overdue: boolean;
    note: string | null;
    user_id: number | null;
    agent_name: string | null;
    /** Who raised the action. Null when it was raised by an integration, or when
     * the creation predates the column and was never recorded against a user. */
    created_by: number | null;
    created_by_name: string | null;
    /**
     * The additional people named on the action, part of the row rather than a
     * write-only field: the edit form seeds itself from the row it was handed, so
     * a row opened from the listing would otherwise submit with its recipients
     * cleared. Not reminder-scoped despite the name — every type can carry them.
     */
    reminder_recipient_type?: string | null;
    reminder_recipient_user_ids?: number[];
    order: SalesActionOrder | null;
}

/**
 * The write contract of `UpdateSalesActionRequest` / `StoreSalesActionRequest`.
 *
 * The three site-inspection keys were missing here while the endpoints accepted
 * them all along, so the interface described less than the contract rather than
 * constraining it. They are required together, and only for that type.
 */
export interface SalesActionPayload {
    action_type?: string;
    schedule_at?: string;
    all_day?: boolean;
    note?: string | null;
    status?: SalesActionStatus;
    completed?: boolean;
    user_id?: number;
    /**
     * The staff member carrying out a site inspection. The same person as
     * `user_id` — one picker, two columns — because `staff_id` is what the
     * scheduler board, the clash check and the field-staff app read.
     */
    staff_id?: number | null;
    duration?: number;
    site_inspection_type?: string | null;
    reminder_recipient_type?: string;
    reminder_recipient_user_ids?: number[] | null;
}

/** One entry a batch endpoint could not honour, reported rather than thrown. */
export interface SalesActionFailure {
    id: number;
    ref: string | null;
    reason: string;
}

export interface BatchCreateResult {
    created: number[];
    created_count: number;
    failed: SalesActionFailure[];
    message: string;
}

export interface BatchReassignResult {
    reassigned: number[];
    reassigned_count: number;
    failed: SalesActionFailure[];
    message: string;
}

/**
 * The derived window belongs to `SalesDiaryAction`, not `SalesAction` — but a
 * reschedule has to move it, because that is what the diary places cards by. It is
 * admitted here explicitly rather than by widening the row type, since these are
 * the only two extra keys any patch has cause to write.
 */
type SalesActionPatch = Partial<SalesAction> & {
    window_start?: string | null;
    window_end?: string | null;
};

/**
 * Apply a patch to one row wherever it sits in a cached payload.
 *
 * Two shapes are handled because the two surfaces hold different ones:
 * `MuvalTable` caches `{ data, meta }`, while a plain list query caches a bare
 * array. Anything else is returned untouched rather than guessed at.
 */
export function patchCachedSalesActions(cached: unknown, id: number, patch: SalesActionPatch): unknown {
    const patchRow = (row: SalesAction): SalesAction => (row?.id === id ? { ...row, ...patch } : row);

    if (Array.isArray(cached)) {
        return cached.map(patchRow);
    }

    if (cached !== null && typeof cached === 'object' && Array.isArray((cached as { data?: unknown }).data)) {
        const page = cached as { data: SalesAction[] };

        return { ...page, data: page.data.map(patchRow) };
    }

    return cached;
}

interface OptimisticContext {
    snapshots: Array<[readonly unknown[], unknown]>;
}

export function useSalesActions() {
    const queryClient = useQueryClient();

    /** The two surfaces that render Sales rows, and can therefore be patched. */
    const patchableKeys = [SALES_ACTIONS_LIST_KEY, SALES_ACTIONS_DIARY_KEY];

    async function applyOptimistic(id: number, patch: SalesActionPatch): Promise<OptimisticContext> {
        // Without this an in-flight refetch can land after the patch and undo it.
        await Promise.all(patchableKeys.map((queryKey) => queryClient.cancelQueries({ queryKey })));

        const snapshots: Array<[readonly unknown[], unknown]> = [];

        patchableKeys.forEach((queryKey) => {
            queryClient.getQueriesData({ queryKey }).forEach((entry) => snapshots.push(entry));
            queryClient.setQueriesData<unknown>({ queryKey }, (cached) => patchCachedSalesActions(cached, id, patch));
        });

        return { snapshots };
    }

    function rollback(context: OptimisticContext | undefined, message: string): void {
        context?.snapshots.forEach(([queryKey, cached]) => queryClient.setQueryData(queryKey, cached));
        toast.error(message);
    }

    function invalidateAll(): void {
        [...patchableKeys, MY_ORDER_ACTIONS_KEY, ORDER_ACTIONS_KEY, SALES_QUEUE_ACTIVE_KEY].forEach((queryKey) =>
            queryClient.invalidateQueries({ queryKey }),
        );
    }

    async function patchAction(id: number, payload: SalesActionPayload): Promise<SalesAction> {
        const { data } = await window.tenantAxios.patch<{ sales_action: SalesAction }>(
            `${SALES_ACTIONS_URL}/${id}`,
            payload,
        );

        return data.sales_action;
    }

    const updateStatus = useMutation({
        mutationFn: ({ id, status }: { id: number; status: SalesActionStatus }) => patchAction(id, { status }),
        onMutate: ({ id, status }) =>
            applyOptimistic(id, {
                status,
                // The API keeps `completed` in step with `status`; mirroring it
                // here stops the row disagreeing with itself mid-flight. An
                // action that is finished — or called off — is not outstanding,
                // so it is not overdue either. The reverse direction is left to
                // the refetch, which is the only thing that knows the viewer's
                // "today". Cancelling never sets `completed`: the API models it
                // as `completed = false` with a status of its own.
                completed: status === 'COMPLETED',
                ...(status === 'COMPLETED' || status === 'CANCELLED' ? { overdue: false } : {}),
            }),
        onError: (_error, _variables, context) =>
            rollback(context, 'Could not update the status. The row has been restored.'),
        onSettled: invalidateAll,
    });

    const reassignOne = useMutation({
        mutationFn: ({ id, userId }: { id: number; userId: number; agentName?: string | null }) =>
            patchAction(id, { user_id: userId }),
        onMutate: ({ id, userId, agentName }) =>
            applyOptimistic(id, { user_id: userId, agent_name: agentName ?? null }),
        onError: (_error, _variables, context) =>
            rollback(context, 'Could not re-assign the action. The row has been restored.'),
        onSettled: invalidateAll,
    });

    /**
     * Drag-to-reschedule on the diary. `all_day` travels with the moment because
     * the two decide how `schedule_at` is *read*: dropping a card on a timed lane
     * makes it a UTC instant, dropping it on an all-day lane makes it a naive
     * local date.
     */
    const reschedule = useMutation({
        mutationFn: ({
            id,
            scheduleAt,
            allDay,
        }: {
            id: number;
            scheduleAt: string;
            allDay: boolean;
            /**
             * The same move in the shape the diary reads — see
             * `toOptimisticReschedulePatch`. The write contract and the read shape
             * differ, so the caller supplies both rather than this guessing.
             */
            optimistic?: SalesActionPatch;
        }) => patchAction(id, { schedule_at: scheduleAt, all_day: allDay }),
        onMutate: ({ id, scheduleAt, allDay, optimistic }) =>
            applyOptimistic(id, optimistic ?? { schedule_at: scheduleAt, all_day: allDay }),
        onError: (_error, _variables, context) =>
            rollback(context, 'Could not reschedule the action. It has been put back.'),
        onSettled: invalidateAll,
    });

    const updateAction = useMutation({
        mutationFn: ({ id, payload }: { id: number; payload: SalesActionPayload }) => patchAction(id, payload),
        // The recipient fields are part of the row now — `SalesActionResource`
        // emits both — so they are no longer stripped from the patch. Derived
        // fields the payload cannot supply (an assignee's name, the overdue flag)
        // still settle on the refetch.
        onMutate: ({ id, payload }) => applyOptimistic(id, payload),
        onError: (_error, _variables, context) =>
            rollback(context, 'Could not save the action. Your changes have been undone.'),
        onSettled: invalidateAll,
    });

    /**
     * Creates get no optimistic row: the listing is paginated and sorted by
     * scheduled time, so where a new action belongs — or whether it belongs on
     * the current page at all — is not something the client can work out.
     */
    const createAction = useMutation({
        mutationFn: async ({ orderId, payload }: { orderId: number; payload: SalesActionPayload }) => {
            const { data } = await window.tenantAxios.post<{ sales_action: SalesAction }>(
                `/sales/orders/${orderId}/order-actions`,
                payload,
            );

            return data.sales_action;
        },
        onError: () => toast.error('Could not create the action.'),
        onSettled: invalidateAll,
    });

    /**
     * Partial success is the contract on both batch endpoints: the caller reads
     * `failed` and summarises it, so a rejected entry is never an error here.
     */
    const batchCreate = useMutation({
        mutationFn: async ({ orderIds, payload }: { orderIds: number[]; payload: SalesActionPayload }) => {
            const { data } = await window.tenantAxios.post<BatchCreateResult>(`${SALES_ACTIONS_URL}/batch`, {
                order_ids: orderIds,
                ...payload,
            });

            return data;
        },
        onError: () => toast.error('Could not create the actions.'),
        onSettled: invalidateAll,
    });

    const batchReassign = useMutation({
        mutationFn: async ({ orderActionIds, userId }: { orderActionIds: number[]; userId: number }) => {
            const { data } = await window.tenantAxios.post<BatchReassignResult>(`${SALES_ACTIONS_URL}/reassign`, {
                order_action_ids: orderActionIds,
                user_id: userId,
            });

            return data;
        },
        onError: () => toast.error('Could not re-assign the actions.'),
        onSettled: invalidateAll,
    });

    return {
        updateStatus,
        reassignOne,
        reschedule,
        updateAction,
        createAction,
        batchCreate,
        batchReassign,
        invalidateAll,
    };
}
